import {
  createAsyncThunk,
  createSlice,
  SerializedError,
} from "@reduxjs/toolkit";
import taskServices from "../../../services/tasks";

type Task = {
  tasks: any[] | null;
  currentTasks: number;
  currentSet: any;
  message: string;
  loading: boolean;
  error: null | SerializedError | string | undefined;
  completedSet: any[] | null;
};

// Initial state
const initialState: Task = {
  tasks: null,
  loading: false,
  currentTasks: 1,
  error: null,
  message: "",
  currentSet: {},
  completedSet: null,
};

// Helper function to handle errors
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return "An unknown error occurred";
  }
};

export const fetchTasks = createAsyncThunk(
  "task/fetchTasks",
  async (_, { rejectWithValue }) => {
    try {
      const response = await taskServices.getAll();
      return response.data?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);

export const compliteTask = async ({
  setId,
  taskId,
}: {
  setId: string;
  taskId: string;
}) => {
  try {
    const response = await taskServices.compliteTask(setId, taskId);
    if (response.status == 200) {
      return {
        message: response.data?.message,
        status: 200,
      };
    } else return { message: "the task is already completed", status: 400 };
  } catch (error: any) {
    throw new Error(error?.response?.data?.message||error?.message);
  }
};
export const getUsersAssignedTask = createAsyncThunk(
  "task/userAssignedTask",
  async (id: string, { rejectWithValue }) => {
    try {
      if (id) {
        const response = await taskServices.getUsersAssignedSet(id);
        return response.data?.data;
      }
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
export const assignTask = createAsyncThunk(
  "task/assignTask",
  async (
    {
      userId,
      data,
    }: {
      data: {
        albumId: string;
        freezingAmount: number;
        comboCommision: number;
      }[];
      userId: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await taskServices.assignTask(userId, data);
      if (response.data?.sucess) {
        return response.data?.set;
      } else {
        return rejectWithValue(response?.data?.message);
      }
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error?.message));
    }
  }
);

export const getSingleTasks = createAsyncThunk(
  "task/getSingleTasks",
  async (
    { userId, setId }: { userId: string; setId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await taskServices.getAssignedTask(userId, setId);
      return response.data?.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getUsersTaskByAdmin = createAsyncThunk(
  "task/userAssignedTaskbyadmin",
  async (id: string, { rejectWithValue }) => {
    try {
      if (id) {
        const response = await taskServices.getUsersAssignedSet(id);
        return response.data?.data;
      }
    } catch (error: any) {
      return rejectWithValue(getErrorMessage(error?.response?.data?.message));
    }
  }
);
export const deleteTaskHandler = createAsyncThunk(
  "task/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await taskServices.deleteTask(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);
export const unFreezeUser = createAsyncThunk(
  "/taks/unfreeze",
  async (
    {
      id,
      freezingTaskId,
      freezingSetId,
    }: { id: string; freezingTaskId: string; freezingSetId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await taskServices.handleUnFreezeUser(
        id,
        freezingSetId,
        freezingTaskId
      );
      return response.data.data;
    } catch (error: any) {
      rejectWithValue(error?.response?.data?.message);
    }
  }
);
export const getComplitedSet = createAsyncThunk(
  "task/completed set",
  async (_, { rejectWithValue }) => {
    try {
      const response = await taskServices.getCompletedSet();
      return response.data.complitedSets;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  }
);
export const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setInitialSetState: (state) => {
      return {
        ...state,
        loading: false,
        currentTasks: 1,
        error: null,
        message: "",
        currentSet: {},
        tasks: [],
      };
    },
    dismissMessage: (state) => {
      return {
        ...state,
        message: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload.filter(
          (post: { status: string }) => post?.status !== "completed"
        );
        state.currentTasks =
          action.payload?.length -
          action.payload.filter(
            (post: { status: string }) => post?.status === "completed"
          )?.length;
        state.error = null;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(assignTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignTask.fulfilled, (state, action) => {
        state.tasks = Array.isArray(state.tasks)
          ? [...state.tasks, action.payload.tasks]
          : [action.payload.tasks];
      })

      .addCase(assignTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSingleTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSingleTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSet = action.payload;
        state.error = null;
      })
      .addCase(getSingleTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getUsersAssignedTask.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersAssignedTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload?.filter(
          (set: any) => set?.status !== "completed"
        );
        state.error = null;
      })
      .addCase(getUsersAssignedTask.rejected, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getUsersTaskByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersTaskByAdmin.fulfilled, (state, action: any) => {
        state.loading = false;
        state.tasks = action.payload;
        state.error = null;
      })
      .addCase(getUsersTaskByAdmin.rejected, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(unFreezeUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unFreezeUser.fulfilled, (state, action: any) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(unFreezeUser.rejected, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteTaskHandler.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTaskHandler.fulfilled, (state, action: any) => {
        state.loading = false;
        state.error = null;
        state.tasks = Array.isArray(state.tasks)
          ? state.tasks.filter(
              (task: any) => task?._id === action?.payload?._id
            )
          : [];
      })
      .addCase(getComplitedSet.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.completedSet = action.payload;
      })
      .addCase(getComplitedSet.rejected, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getComplitedSet.pending, (state) => {
        state.loading = true;
        state.error = null;
      });
  },
});

// Selectors
export const selectLoading = (state: any) => state.tasks.loading;
export const selectCurrentset = (state: any) => state.tasks.currentTasks;
export const selectAllTasks = (state: any) => state.tasks.tasks;
export const selectTaskErrors = (state: any) => state.tasks.error;
export const selectCurrentSet = (state: any) => state.tasks.currentSet;
export const selectMessage = (state: any) => state.tasks.message;
export const selectUserCompletedSet = (state: any) => state.tasks.completedSet;
export const { setInitialSetState, dismissMessage } = taskSlice.actions;
export default taskSlice.reducer;
